import { TextBlock } from '../../../components/1elements'
import uuid from 'react-uuid'
import { useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles';

import GenericMap from '../googleMap/genericMap'
import {
  findShapeCenter,
  WicketToGoogle,
  GoogleToWicket
} from '../pickList/WicketUtils'
import { drawOneShape } from './drawingManager'
import CircleEdit from './MapEdits/CircleEdit'
import PolyEdit from './MapEdits/PolyEdit'
import RecTangleEdit from './MapEdits/RecTangleEdit'

const MapSearch = ({ bucket }) => {
  const classes = useStyles();

  const [googleBagInLocalState, setGoogleBagInLocalState] = useState(
    () => Object.fromEntries(WicketToGoogle(bucket.fullSelection).map(shape => [shape.id, shape]))
  )
  const shapeDataRef = useRef(googleBagInLocalState)
  const [polygonEditVersion, setPolygonEditVersion] = useState(0)

  // Keep the initial center reference stable. Passing a newly-created center
  // object after every shape update makes Google Maps re-center the viewport.
  const [center] = useState(() =>
    findShapeCenter(WicketToGoogle(bucket.fullSelection))
  )

  bucket.tempChanges.presaveProcessor = rawData => {
    const googleFormattedArray = []
    for (const property in rawData) {
      if (rawData.hasOwnProperty(property))
        googleFormattedArray.push(rawData[property])
    }

    const wicketFormattedArray = GoogleToWicket(googleFormattedArray)

    console.log('presave status3', wicketFormattedArray)

    return wicketFormattedArray
  }

  const mapTriggeredShapeModificationHandler = shapeData => {
    tripleUpdates(shapeData, false)
    if (shapeData.type === 'polygon') {
      setPolygonEditVersion(version => version + 1)
    }
  }
  const textTriggeredShapeModificationHandler = (partialShape, updateMap) => {
    tripleUpdates(partialShape, updateMap)
  }
  const localDrawOneShapeWrapper = oneShape => {
    const lastHandle = bucket.tempChanges.shapeHandleBag[oneShape.id]
    if (lastHandle) {
      lastHandle.setMap(null)
    } else {
      console.log('no handle')
    }

    const newShapeHandle = drawOneShape(
      bucket.tempChanges.mapHandle,
      oneShape,
      mapTriggeredShapeModificationHandler
    )
    bucket.tempChanges.shapeHandleBag[oneShape.id] = newShapeHandle
  }

  /// take one or more shapes in google format, update it google representation (which triggers the textual representation change), and redraw the changed shapes
  const tripleUpdates = (googleShapeOrList, redrawOnMap = true) => {
    // Google Maps listeners outlive the render that registered them.
    const newGoogleBag = { ...shapeDataRef.current }

    const dealWithOneShape = oneGoogleShape => {
      // deal with the format google wants
      const existingGoogleShape = newGoogleBag[oneGoogleShape.id]
      const mergedGoogleShape = existingGoogleShape
        ? { ...existingGoogleShape, ...oneGoogleShape }
        : oneGoogleShape
      newGoogleBag[mergedGoogleShape.id] = mergedGoogleShape
      if (redrawOnMap) {
        localDrawOneShapeWrapper(mergedGoogleShape)
      }
    }

    if (Array.isArray(googleShapeOrList)) {
      googleShapeOrList.forEach(oneShape => {
        dealWithOneShape(oneShape)
      })
    } else {
      dealWithOneShape(googleShapeOrList)
    }

    shapeDataRef.current = newGoogleBag
    setGoogleBagInLocalState(newGoogleBag)
    bucket.tempChanges.data = newGoogleBag
  }

  const oneTimeInitialization = mapHandle => {
    bucket.tempChanges.mapHandle = mapHandle
    const worksetInGoogleFormat = WicketToGoogle(bucket.fullSelection)
    tripleUpdates(worksetInGoogleFormat, true)
  }

  const cleanUpMemory = () => {
    Object.values(bucket.tempChanges.shapeHandleBag).forEach(o => o.setMap(null))
    bucket.tempChanges.shapeHandleBag = {}
  }

  /// Newly drawn shapes don't have the right interface to work with subsequent edits
  /// hence they get deleted and redrawn from another interface as soon as they get created, this way later code can
  /// just work with one interface and hence make the code much more maintainable overall
  const pathFinder = data => {
    switch (data.type) {
      case 'polygon':
        {
          const poly = []
          const pathArray = data.overlay.getPath().getArray();
          pathArray?.forEach(o => {
            poly.push({ lat: o.lat(), lng: o.lng() })
          })
          tripleUpdates({
            name: 'generic polygon',
            type: data.type,
            coords: poly,
            id: uuid(),
            isNew: true
          })
        }

        break
      case 'circle': {
        tripleUpdates({
          name: 'generic circle',
          type: data.type,
          center: {
            lat: data.overlay.center.lat(),
            lng: data.overlay.center.lng()
          },
          radius: data.overlay.radius,
          id: uuid(),
          isNew: true
        })
        break
      }

      case 'rectangle': {
        const bounds = {
          north: data.overlay.bounds.Ua.hi,
          west: data.overlay.bounds.Ha.lo,
          south: data.overlay.bounds.Ua.lo,
          east: data.overlay.bounds.Ha.hi
        }
        tripleUpdates({
          name: 'generic rectangle',
          type: data.type,
          bounds,
          id: uuid(),
          isNew: true
        })
        break
      }
      default:
        throw new Error('unsupported shape type')
    }
    data.overlay.setMap(null)
  }

  const renderTexualPresentationOfOneShape = (oneShape, index) => {
    switch (oneShape.type) {
      case 'circle':
        return (
          <CircleEdit
            key={index}
            shape={oneShape}
            setShape={textTriggeredShapeModificationHandler}
          />
        )
      case 'rectangle':
        return (
          <RecTangleEdit
            key={index}
            shape={oneShape}
            setShape={textTriggeredShapeModificationHandler}
          />
        )
      case 'polygon':
        return (
          <PolyEdit
            key={index}
            shape={oneShape}
            setShape={textTriggeredShapeModificationHandler}
          />
        )

      default:
        throw new Error('unrecognized shape')
    }
  }
  return (
    <div className={classes.root}>
      <div className={classes.sidebar}>
        <TextBlock heading="Geo Fencing Textual Data:">
          {googleBagInLocalState &&
            Object.values(googleBagInLocalState).map((oneShape, index) =>
              renderTexualPresentationOfOneShape(oneShape, index)
            )}
        </TextBlock>
      </div>
      <div className={classes.mapArea}>
        <GenericMap
          zoom={10}
          center={center}
          mapContainerStyle={{ width: '100%', height: '70vh' }}
          onLoad={oneTimeInitialization}
          onUnmount={cleanUpMemory}
          pathFinder={pathFinder}
          polygonEditVersion={polygonEditVersion}
          bucket={bucket}
        />
      </div>
    </div>
  )
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    columnGap: '10px',
    justifyContent: 'space-between',
  },
  sidebar: {
    width: '20%',
    maxHeight: '70vh',
    height: '100%',
    overflowY: 'auto',
    paddingRight: '5px',
  },
  mapArea: {
    width: '80%',
  },
});

export default MapSearch
